import { SoccerStages } from '@adflow/types';

import type {
  SoccerCompetitionId,
  SoccerFullTimeResultOdds,
  SoccerFixture,
  SoccerFixtureDefinition,
  SoccerPostEventOutcome,
} from './types';

export const SOCCER_COMPETITION_ORDER: ReadonlyArray<SoccerCompetitionId> = [
  'WORLD_CUP',
  'PREMIER_LEAGUE',
  'LA_LIGA',
];

type CompetitionTeams = {
  label: string;
  home: { name: string; abbreviation: string; competitorId: string };
  away: { name: string; abbreviation: string; competitorId: string };
};

const COMPETITION_TEAMS: Record<SoccerCompetitionId, CompetitionTeams> = {
  WORLD_CUP: {
    label: 'World Cup',
    home: { name: 'Portugal', abbreviation: 'POR', competitorId: 'sr:competitor:4704' },
    away: { name: 'France', abbreviation: 'FRA', competitorId: 'sr:competitor:4481' },
  },
  PREMIER_LEAGUE: {
    label: 'Premier League',
    home: { name: 'Manchester United', abbreviation: 'MUN', competitorId: 'sr:competitor:35' },
    away: { name: 'Liverpool', abbreviation: 'LIV', competitorId: 'sr:competitor:44' },
  },
  LA_LIGA: {
    label: 'LaLiga',
    home: { name: 'Real Madrid', abbreviation: 'RMA', competitorId: 'sr:competitor:2829' },
    away: { name: 'Barcelona', abbreviation: 'BAR', competitorId: 'sr:competitor:2817' },
  },
};

const buildFixtureBase = <Stage extends SoccerStages>(
  stage: Stage,
  competition: SoccerCompetitionId,
) => {
  const teams = COMPETITION_TEAMS[competition];
  return {
    id: `${stage}__${competition}`,
    stage,
    competition,
    competitionLabel: teams.label,
    homeTeamName: teams.home.name,
    awayTeamName: teams.away.name,
    homeTeamAbbreviation: teams.home.abbreviation,
    awayTeamAbbreviation: teams.away.abbreviation,
    homeCompetitorId: teams.home.competitorId,
    awayCompetitorId: teams.away.competitorId,
  };
};

const MINUTE = 60 * 1000;
const DAY = 24 * 60 * MINUTE;

const PRE_EVENT_KICKOFF_OFFSETS_MS: Record<SoccerCompetitionId, number> = {
  WORLD_CUP: 30 * MINUTE,
  PREMIER_LEAGUE: DAY,
  LA_LIGA: 5 * DAY,
};

const buildFullTimeResultOdds = (
  homeOdds: readonly [number, number],
  awayOdds: readonly [number, number],
  drawOdds: readonly [number, number],
): SoccerFullTimeResultOdds => ({
  HOME_ODDS: homeOdds,
  AWAY_ODDS: awayOdds,
  DRAW_ODDS: drawOdds,
  COMPETITOR_ODDS: homeOdds,
  OPPONENT_ODDS: awayOdds,
});

const PRE_EVENT_FULL_TIME_RESULT_ODDS: Record<SoccerCompetitionId, SoccerFullTimeResultOdds> = {
  WORLD_CUP: buildFullTimeResultOdds([5, 4], [21, 10], [23, 10]),
  PREMIER_LEAGUE: buildFullTimeResultOdds([6, 5], [9, 4], [12, 5]),
  LA_LIGA: buildFullTimeResultOdds([4, 5], [13, 4], [11, 5]),
};

const LIVE_FULL_TIME_RESULT_ODDS: Record<SoccerCompetitionId, SoccerFullTimeResultOdds> = {
  WORLD_CUP: buildFullTimeResultOdds([7, 5], [2, 1], [8, 5]),
  PREMIER_LEAGUE: buildFullTimeResultOdds([11, 10], [13, 5], [9, 4]),
  LA_LIGA: buildFullTimeResultOdds([7, 2], [3, 4], [12, 5]),
};

const buildScoredFixture = <Stage extends Exclude<SoccerStages, SoccerStages.PRE_EVENT>>(
  stage: Stage,
  competition: SoccerCompetitionId,
  homeScore: string,
  awayScore: string,
) => ({
  ...buildFixtureBase(stage, competition),
  homeScore,
  awayScore,
});

const buildPostFixture = <Stage extends SoccerStages.POST_EVENT | SoccerStages.POST_WINNER>(
  stage: Stage,
  competition: SoccerCompetitionId,
  homeScore: string,
  awayScore: string,
  postEventOutcome: SoccerPostEventOutcome,
) => ({
  ...buildScoredFixture(stage, competition, homeScore, awayScore),
  postEventOutcome,
});

export const SOCCER_FIXTURES: ReadonlyArray<SoccerFixtureDefinition> = [
  {
    ...buildFixtureBase(SoccerStages.PRE_EVENT, 'WORLD_CUP'),
    kickoffOffsetMs: PRE_EVENT_KICKOFF_OFFSETS_MS.WORLD_CUP,
    matchStatus: 'not_started',
    fullTimeResultOdds: PRE_EVENT_FULL_TIME_RESULT_ODDS.WORLD_CUP,
  },
  {
    ...buildFixtureBase(SoccerStages.PRE_EVENT, 'PREMIER_LEAGUE'),
    kickoffOffsetMs: PRE_EVENT_KICKOFF_OFFSETS_MS.PREMIER_LEAGUE,
    matchStatus: 'not_started',
    fullTimeResultOdds: PRE_EVENT_FULL_TIME_RESULT_ODDS.PREMIER_LEAGUE,
  },
  {
    ...buildFixtureBase(SoccerStages.PRE_EVENT, 'LA_LIGA'),
    kickoffOffsetMs: PRE_EVENT_KICKOFF_OFFSETS_MS.LA_LIGA,
    matchStatus: 'not_started',
    fullTimeResultOdds: PRE_EVENT_FULL_TIME_RESULT_ODDS.LA_LIGA,
  },
  {
    ...buildScoredFixture(SoccerStages.LIVE, 'WORLD_CUP', '0', '0'),
    clock: '78:24',
    matchStatus: '2nd_half',
    fullTimeResultOdds: LIVE_FULL_TIME_RESULT_ODDS.WORLD_CUP,
  },
  {
    ...buildScoredFixture(SoccerStages.LIVE, 'PREMIER_LEAGUE', '1', '0'),
    clock: '45:00',
    matchStatus: 'halftime',
    fullTimeResultOdds: LIVE_FULL_TIME_RESULT_ODDS.PREMIER_LEAGUE,
  },
  {
    ...buildScoredFixture(SoccerStages.LIVE, 'LA_LIGA', '0', '2'),
    clock: '12:05',
    matchStatus: 'penalties',
    fullTimeResultOdds: LIVE_FULL_TIME_RESULT_ODDS.LA_LIGA,
    homePenaltyScore: '4',
    awayPenaltyScore: '2',
  },
  {
    ...buildScoredFixture(SoccerStages.LIVE_GOAL, 'WORLD_CUP', '1', '0'),
    clock: '18:32',
    matchStatus: '1st_half',
    goalScorerName: 'C. Ronaldo',
    goalMatchTime: '18',
    goalTeamCompetitorId: COMPETITION_TEAMS.WORLD_CUP.home.competitorId,
  },
  {
    ...buildScoredFixture(SoccerStages.LIVE_GOAL, 'PREMIER_LEAGUE', '1', '1'),
    clock: '67:15',
    matchStatus: '2nd_half',
    goalScorerName: 'Sazbolai',
    goalMatchTime: '67',
    goalTeamCompetitorId: COMPETITION_TEAMS.PREMIER_LEAGUE.away.competitorId,
  },
  {
    ...buildScoredFixture(SoccerStages.LIVE_GOAL, 'LA_LIGA', '1', '2'),
    clock: '72:48',
    matchStatus: '2nd_half',
    goalScorerName: 'Lamine Yamal',
    goalMatchTime: '72',
    goalTeamCompetitorId: COMPETITION_TEAMS.LA_LIGA.away.competitorId,
  },
  {
    ...buildPostFixture(SoccerStages.POST_EVENT, 'WORLD_CUP', '2', '2', 'win'),
    matchStatus: 'penalties',
    homePenaltyScore: '3',
    awayPenaltyScore: '3',
  },
  {
    ...buildPostFixture(SoccerStages.POST_EVENT, 'PREMIER_LEAGUE', '1', '1', 'draw'),
    matchStatus: 'ended',
  },
  {
    ...buildPostFixture(SoccerStages.POST_EVENT, 'LA_LIGA', '0', '2', 'loss'),
    matchStatus: 'ended',
  },
  {
    ...buildPostFixture(SoccerStages.POST_WINNER, 'WORLD_CUP', '1', '0', 'win'),
    matchStatus: 'aet',
    winnerTeamName: COMPETITION_TEAMS.WORLD_CUP.home.name,
    winnerTeamCompetitorId: COMPETITION_TEAMS.WORLD_CUP.home.competitorId,
  },
  {
    ...buildPostFixture(SoccerStages.POST_WINNER, 'PREMIER_LEAGUE', '2', '2', 'win'),
    matchStatus: 'ap',
    winnerTeamName: COMPETITION_TEAMS.PREMIER_LEAGUE.home.name,
    winnerTeamCompetitorId: COMPETITION_TEAMS.PREMIER_LEAGUE.home.competitorId,
    homePenaltyScore: '4',
    awayPenaltyScore: '2',
  },
  {
    ...buildPostFixture(SoccerStages.POST_WINNER, 'LA_LIGA', '3', '1', 'win'),
    matchStatus: 'ended',
    winnerTeamName: COMPETITION_TEAMS.LA_LIGA.home.name,
    winnerTeamCompetitorId: COMPETITION_TEAMS.LA_LIGA.home.competitorId,
  },
];

const materializeFixture = (fixture: SoccerFixtureDefinition, activatedAt: Date): SoccerFixture => {
  if (fixture.stage !== SoccerStages.PRE_EVENT) {
    return fixture;
  }

  const { kickoffOffsetMs, ...rest } = fixture;
  return {
    ...rest,
    kickoffAt: new Date(activatedAt.getTime() + kickoffOffsetMs),
  };
};

export const getFixturesForStage = (
  stage: SoccerStages,
  activatedAt = new Date(),
): ReadonlyArray<SoccerFixture> =>
  SOCCER_FIXTURES.filter((fixture) => fixture.stage === stage).map((fixture) =>
    materializeFixture(fixture, activatedAt),
  );

export const getFixtureLabel = (fixture: SoccerFixture): string =>
  `${fixture.homeTeamName} vs ${fixture.awayTeamName} - ${fixture.competitionLabel}`;
